import React from 'react';
import DodTermText from './DodTermText';
import type { DodGlossaryTermId } from './dodGlossary';
import {
  OFAC_DASHBOARD_WIDGETS,
  createOfacDashboard,
  dashboardsForAccount,
  loadOfacDashboards,
  ofacDashboardWidgetView,
  saveOfacDashboards,
  toggleOfacDashboardWidget,
  type OfacDashboardLive,
  type OfacDashboardNavTarget,
  type OfacDashboardRecord,
  type OfacDashboardWidgetId,
} from './ofacDashboards';
import { readAccountPreference, writeAccountPreference } from './dashboardWidgets';
import './OfacDashboardsPanel.css';

export type OfacWorkspaceTool = {
  id: string;
  label: string;
};

type OfacWorkspaceBarProps = {
  language: 'en' | 'es';
  active: string;
  items: OfacWorkspaceTool[];
  onOpen: (id: string) => void;
};

/** Screening tools live on the dashboard, not in a second sidebar menu. */
export const OfacWorkspaceBar: React.FC<OfacWorkspaceBarProps> = ({
  language,
  active,
  items,
  onOpen,
}) => {
  const es = language === 'es';
  const purposeOrder = ['ofacDashboards', 'ofacScreening', 'ofacInvestigations', 'ofacLiveFeed', 'ofacWhitelist', 'ofacCapta'];
  const ordered = [...items].sort((left, right) => purposeOrder.indexOf(left.id) - purposeOrder.indexOf(right.id));
  const icon = (id: string) => {
    if (id === 'ofacScreening') {
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M14.5 14.5 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>;
    }
    if (id === 'ofacInvestigations') {
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5 20 8.2v7.6L12 20.5 4 15.8V8.2L12 3.5Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>;
    }
    if (id === 'ofacLiveFeed') {
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12h3l2-4 3 8 2-4h6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    }
    if (id === 'ofacWhitelist') {
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M8 12.2 10.8 15 16 9.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    }
    if (id === 'ofacCapta') {
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10h16M6 10V19M10 10V19M14 10V19M18 10V19M3 19h18M12 4 4 10h16L12 4Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>;
    }
    return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>;
  };
  return (
    <div className="ofac-workspace">
      <div className="ofac-workspace-bar" role="tablist" aria-label={es ? 'Trabajos de sanciones' : 'Sanctions jobs'}>
        {ordered.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active === item.id}
            aria-label={item.label}
            className={active === item.id ? 'is-selected' : ''}
            onClick={() => onOpen(item.id)}
          >
            {icon(item.id)}
            <span className="ofac-tip" aria-hidden="true">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

type OfacAccountOption = {
  id: string;
  name: string;
};

type OfacDashboardsPanelProps = {
  language: 'en' | 'es';
  accountId: string;
  accountLabel: string;
  live: OfacDashboardLive;
  onOpen: (nav: OfacDashboardNavTarget) => void;
  gatewayBaseUrl?: string;
  accessToken?: string;
};

const OfacDashboardsPanel: React.FC<OfacDashboardsPanelProps> = ({
  language,
  accountId,
  accountLabel,
  live,
  onOpen,
  gatewayBaseUrl,
  accessToken,
}) => {
  const es = language === 'es';
  const [dashboards, setDashboards] = React.useState<OfacDashboardRecord[]>(() => loadOfacDashboards(window.localStorage));
  const [activeId, setActiveId] = React.useState<string>('');
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [rename, setRename] = React.useState('');
  const [accounts, setAccounts] = React.useState<OfacAccountOption[]>([]);
  const [accountsLoaded, setAccountsLoaded] = React.useState(!accessToken);
  const [selectedAccountId, setSelectedAccountId] = React.useState('');
  const [prefsReady, setPrefsReady] = React.useState(false);
  const toggleTerms = new Set<DodGlossaryTermId>();

  React.useEffect(() => {
    const apiBase = String(gatewayBaseUrl || '').replace(/\/$/, '');
    if (!apiBase || !accessToken) {
      setAccountsLoaded(true);
      return undefined;
    }
    let cancelled = false;
    const loadAccounts = async () => {
      try {
        const response = await fetch(`${apiBase}/api/v1/compliance-consoles/ofac-compliance/accounts`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) {
          return;
        }
        const body = await response.json();
        const rows = Array.isArray(body?.accounts) ? body.accounts : [];
        const next = rows
          .map((row: { company_id?: number; company_name?: string }) => ({
            id: String(row.company_id || ''),
            name: String(row.company_name || '').trim(),
          }))
          .filter((row: OfacAccountOption) => row.id && row.name && row.name.toLowerCase() !== 'keycloak');
        if (!cancelled) {
          setAccounts(next);
          let savedId = '';
          if (accessToken) {
            const pref = await readAccountPreference(apiBase, accessToken, 'console-account:ofac-dashboards');
            if (pref.ok && pref.value && typeof pref.value === 'object') {
              savedId = String((pref.value as { companyId?: number }).companyId || '');
            }
          }
          if (!cancelled) {
            setSelectedAccountId((current) => {
              if (next.some((row: OfacAccountOption) => row.id === current)) {
                return current;
              }
              if (savedId && next.some((row: OfacAccountOption) => row.id === savedId)) {
                return savedId;
              }
              return next.length === 1 ? next[0].id : '';
            });
          }
        }
      } catch {
        /* the signed-in account label remains the fallback */
      } finally {
        if (!cancelled) {
          setAccountsLoaded(true);
        }
      }
    };
    void loadAccounts();
    return () => {
      cancelled = true;
    };
  }, [accessToken, gatewayBaseUrl]);

  React.useEffect(() => {
    const base = String(gatewayBaseUrl || '').replace(/\/$/, '');
    if (!base || !accessToken) {
      return undefined;
    }
    let cancelled = false;
    const loadPrefs = async () => {
      const result = await readAccountPreference(base, accessToken, 'ofac-dashboards');
      if (cancelled || !result.ok) {
        return;
      }
      if (Array.isArray(result.value)) {
        const remote = loadOfacDashboards({ getItem: () => JSON.stringify(result.value) });
        setDashboards(remote);
      }
      setPrefsReady(true);
    };
    void loadPrefs();
    return () => {
      cancelled = true;
    };
  }, [accessToken, gatewayBaseUrl]);

  const pickedAccountId = accounts.some((row) => row.id === selectedAccountId) ? selectedAccountId : '';
  const activeAccountId = pickedAccountId || (accounts.length === 0 ? accountId : '');
  const activeAccountName = accounts.find((row) => row.id === activeAccountId)?.name || accountLabel;
  const mine = dashboardsForAccount(dashboards, activeAccountId).filter((row) => (
    row.name.trim().toLowerCase() !== activeAccountName.trim().toLowerCase()
  ));
  const active = mine.find((row) => row.id === activeId) || mine[0] || null;

  React.useEffect(() => {
    if (!activeAccountName) {
      return;
    }
    setDashboards((current) => {
      const next = current.filter((row) => !(
        row.accountId === activeAccountId
        && row.name.trim().toLowerCase() === activeAccountName.trim().toLowerCase()
      ));
      return next.length === current.length ? current : next;
    });
  }, [activeAccountId, activeAccountName]);

  React.useEffect(() => {
    saveOfacDashboards(window.localStorage, dashboards);
  }, [dashboards]);

  React.useEffect(() => {
    if (!prefsReady) {
      return;
    }
    const base = String(gatewayBaseUrl || '').replace(/\/$/, '');
    if (!base || !accessToken) {
      return;
    }
    void writeAccountPreference(base, accessToken, 'ofac-dashboards', dashboards);
  }, [accessToken, dashboards, gatewayBaseUrl, prefsReady]);

  const persist = (next: OfacDashboardRecord) => {
    setDashboards((current) => {
      const rest = current.filter((row) => row.id !== next.id);
      return [...rest, next];
    });
    setActiveId(next.id);
  };

  const createReady = accounts.length > 0
    ? Boolean(pickedAccountId)
    : Boolean(activeAccountId) && (!accessToken || accountsLoaded);
  const create = () => {
    if (!createReady || !activeAccountId || mine.length) {
      return;
    }
    const created = createOfacDashboard(activeAccountId, es ? 'Panel de sanciones' : 'Sanctions dashboard');
    persist(created);
    setSettingsOpen(false);
  };

  const remove = (id: string) => {
    setDashboards((current) => current.filter((row) => row.id !== id));
    if (activeId === id) {
      setActiveId('');
    }
  };

  const toggle = (widgetId: OfacDashboardWidgetId, included: boolean) => {
    if (!active) {
      return;
    }
    persist(toggleOfacDashboardWidget(active, widgetId, included));
  };

  const cards = active
    ? active.widgetIds.map((id) => ofacDashboardWidgetView(id, live, language))
    : [];
  const volume = live.volumePoints || [];
  const exceptions = live.exceptionPoints || [];
  const spark = (values: number[]): string => {
    if (values.length < 2) {
      return '';
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = Math.max(1, max - min);
    return values.map((value, index) => {
      const x = (index / (values.length - 1)) * 220;
      const y = 40 - ((value - min) / span) * 32;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  };
  const picture = [
    { id: 'screened', label: es ? 'Evaluados' : 'Screened', value: live.screened30d, tone: 'neutral' },
    { id: 'clear', label: es ? 'Sin coincidencia' : 'Clear', value: live.clearCount || 0, tone: 'ok' },
    { id: 'review', label: es ? 'Revisión' : 'Review', value: live.reviewCount || 0, tone: 'warn' },
    { id: 'hit', label: es ? 'Posible coincidencia' : 'Possible match', value: live.hitCount || 0, tone: 'alert' },
  ];

  return (
    <section className="ofac-dashboards" aria-label={es ? 'Panel de sanciones' : 'Sanctions dashboard'}>
      <header className="ofac-dashboards-intro">
        <h2>{active?.name || (es ? 'Paneles de sanciones' : 'Sanctions dashboards')}</h2>
        <div className="ofac-dashboards-actions">
          <label className="ofac-account-pick" htmlFor="ofac-subscription-account">
            <span>{es ? 'Cuenta' : 'Account'}</span>
            <select
              id="ofac-subscription-account"
              aria-label={es ? 'Cuenta' : 'Account'}
              value={pickedAccountId}
              onChange={(event) => {
                const nextId = event.target.value;
                setSelectedAccountId(nextId);
                setActiveId('');
                setSettingsOpen(false);
                const base = String(gatewayBaseUrl || '').replace(/\/$/, '');
                if (base && accessToken && nextId) {
                  void writeAccountPreference(base, accessToken, 'console-account:ofac-dashboards', { companyId: Number(nextId) });
                }
              }}
            >
              <option value="">{es ? 'Seleccionar cuenta' : 'Select account'}</option>
              {accounts.map((row) => (
                <option key={row.id} value={row.id}>{row.name}</option>
              ))}
            </select>
          </label>
          {mine.length === 0 ? (
            <button
              type="button"
              className="dashboard-btn dashboard-btn-primary ofac-create-dash"
              aria-label={es ? 'Crear panel' : 'Create dashboard'}
              disabled={!createReady}
              onClick={create}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              {es ? 'Crear panel' : 'Create dashboard'}
              <span className="ofac-tip" aria-hidden="true">
                {es ? 'Guarda un panel con nombre en la cuenta elegida' : 'Save a named dashboard on the account you picked'}
              </span>
            </button>
          ) : null}
          {active ? (
            <button
              type="button"
              className={`ofac-icon-btn${settingsOpen ? ' is-on' : ''}`}
              aria-pressed={settingsOpen}
              aria-label={es ? 'Ajustes del panel' : 'Dashboard settings'}
              onClick={() => {
                setRename(active.name);
                setSettingsOpen((open) => !open);
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6 6l1.6 1.6M16.4 16.4 18 18M18 6l-1.6 1.6M7.6 16.4 6 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              <span className="ofac-tip" aria-hidden="true">
                {es ? 'Cambiar el nombre o las tarjetas' : 'Rename this dashboard or choose its cards'}
              </span>
            </button>
          ) : null}
        </div>
      </header>
      {!active ? (
        <p className="ofac-dashboards-empty" role="status">
          {accounts.length > 0 && !pickedAccountId
            ? (es
              ? 'Elija la cuenta. Luego puede nombrar un panel para esa cuenta.'
              : 'Pick the account. Then you can name a dashboard for that account.')
            : (es
              ? 'Todavía no hay un panel con nombre. Los números de arriba son conteos en vivo.'
              : 'No named dashboard is saved yet. The numbers above are live counts.')}
        </p>
      ) : null}
      <section className="ofac-graphic" aria-label={es ? 'Imagen de sanciones' : 'Sanctions picture'}>
        <div className="ofac-graphic-kpis">
          {picture.map((metric) => (
            <div key={metric.id} className={`ofac-graphic-kpi is-${metric.tone}`}>
              <strong>{metric.value}</strong>
              <span>{metric.label}</span>
            </div>
          ))}
        </div>
        {volume.some((value) => value > 0) ? (
          <svg className="ofac-graphic-spark" viewBox="0 0 220 48" role="img" aria-label={es ? 'Tendencia de cribado' : 'Screening trend'}>
            <polyline fill="none" stroke="#1a7a4c" strokeWidth="2.5" points={spark(volume)} />
            {exceptions.length ? (
              <polyline fill="none" stroke="#c45c12" strokeWidth="2" strokeDasharray="4 3" points={spark(exceptions)} />
            ) : null}
          </svg>
        ) : (
          <p>{es ? 'La línea aparece cuando esta empresa criba nombres. Estos números son conteos en vivo, no un panel guardado.' : 'The line appears after this company screens names. These numbers are live counts, not a saved dashboard.'}</p>
        )}
      </section>
      {settingsOpen && active ? (
        <div className="ofac-dashboards-settings">
          <label>
            <span>{es ? 'Nombre' : 'Name'}</span>
            <input value={rename} onChange={(event) => setRename(event.target.value)} />
          </label>
          <button
            type="button"
            className="dashboard-btn"
            onClick={() => {
              const nextName = rename.trim();
              if (!nextName || nextName.toLowerCase() === activeAccountName.trim().toLowerCase()) {
                return;
              }
              persist({ ...active, name: nextName, updatedAt: new Date().toISOString() });
            }}
          >
            {es ? 'Guardar nombre' : 'Save name'}
          </button>
          <button type="button" className="dashboard-btn" onClick={() => { remove(active.id); setSettingsOpen(false); }}>
            {es ? 'Eliminar panel' : 'Delete dashboard'}
          </button>
          <fieldset className="ofac-dashboards-toggles">
            <legend>{es ? 'Tarjetas' : 'Cards'}</legend>
            {OFAC_DASHBOARD_WIDGETS.map((widget) => {
              const included = active.widgetIds.includes(widget.id);
              return (
                <label key={widget.id}>
                  <input
                    type="checkbox"
                    checked={included}
                    onChange={(event) => toggle(widget.id, event.target.checked)}
                  />
                  <DodTermText text={widget.title[language]} language={language} seen={toggleTerms} />
                </label>
              );
            })}
          </fieldset>
        </div>
      ) : null}

      {active ? (
        <>
          {cards.length ? (
            <div className="ofac-dashboards-grid">
              {cards.map((card) => {
                const cardTerms = new Set<DodGlossaryTermId>();
                return (
                <article key={card.id} className={`ofac-dashboard-card is-${card.tone}`}>
                  <h4>
                    <DodTermText text={card.title} language={language} seen={cardTerms} />
                  </h4>
                  <p className="ofac-dashboard-status">{card.status}</p>
                  <p>
                    <DodTermText text={card.blurb} language={language} seen={cardTerms} />
                  </p>
                  <button type="button" className="ofac-icon-btn" aria-label={es ? 'Abrir' : 'Open'} onClick={() => onOpen(card.nav)}>
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    <span className="ofac-tip" aria-hidden="true">{es ? 'Abrir la herramienta de esta tarjeta' : 'Open the tool for this card'}</span>
                  </button>
                </article>
                );
              })}
            </div>
          ) : (
            <p className="ofac-dashboards-empty" role="status">
              {es
                ? 'Todas las tarjetas están ocultas. Vuelva a marcar las que quiera ver.'
                : 'Every card is hidden. Check the ones you want to see.'}
            </p>
          )}
        </>
      ) : null}
    </section>
  );
};

export default OfacDashboardsPanel;
